import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * SearchableSelect — a combobox-style searchable dropdown.
 *
 * Props:
 *   options: Array<{ value: string, label: string, hint?: string }>
 *   value: string
 *   onChange: (value) => void
 *   placeholder: string (text for the "all/none" default option)
 *   allOption: { value: '', label: 'All redditors' } | null  (optional explicit "all" option)
 *   width: number (trigger min width)
 *   id: string (for aria)
 */
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  allOption = null,
  width = 180,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  // Resolve the selected option's label for the trigger
  const selected = useMemo(() => {
    if (allOption && value === allOption.value) return allOption;
    return options.find((o) => String(o.value) === String(value)) || null;
  }, [options, value, allOption]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    function handleDown(e) {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Focus the search input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Reset query when closed
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // Compute panel position relative to trigger (for portal)
  const [panelStyle, setPanelStyle] = useState({ top: 0, left: 0, width });
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelStyle({
        top: rect.bottom + 6,
        left: rect.left,
        width: Math.max(rect.width, 220),
      });
    }
  }, [open]);

  function handleSelect(val) {
    onChange(val);
    setOpen(false);
  }

  const activeBorder = open || (value && (!allOption || value !== allOption.value))
    ? 'var(--rh-orange)'
    : 'var(--rh-border)';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          padding: '5px 10px', borderRadius: 7, fontSize: 13,
          border: `1.5px solid ${activeBorder}`,
          background: '#fff', color: selected ? 'var(--rh-text)' : 'var(--rh-text-muted)',
          outline: 'none', minWidth: width, cursor: 'pointer',
          whiteSpace: 'nowrap', lineHeight: 1.4,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2"
          viewBox="0 0 24 24" style={{ flexShrink: 0, transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed', top: panelStyle.top, left: panelStyle.left,
            width: panelStyle.width, zIndex: 100000,
            background: '#fff', border: '1px solid var(--rh-border)',
            borderRadius: 10, boxShadow: 'var(--rh-shadow-md)',
            display: 'flex', flexDirection: 'column',
            maxHeight: 320, overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search input */}
          <div style={{ padding: 8, borderBottom: '1px solid var(--rh-border)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="13" height="13" fill="none" stroke="var(--rh-text-muted)" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              style={{
                flex: 1, border: 'none', outline: 'none', fontSize: 13,
                color: 'var(--rh-text)', background: 'transparent',
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--rh-text-muted)', display: 'flex' }}
                aria-label="Clear search"
              >
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {/* Options list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {allOption && (!query || allOption.label.toLowerCase().includes(query.trim().toLowerCase())) && (
              <button
                type="button"
                onClick={() => handleSelect(allOption.value)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  width: '100%', padding: '8px 12px', border: 'none', background: 'transparent',
                  cursor: 'pointer', textAlign: 'left', fontSize: 13,
                  color: value === allOption.value ? 'var(--rh-orange)' : 'var(--rh-text)',
                  fontWeight: value === allOption.value ? 600 : 500,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--rh-bg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span>{allOption.label}</span>
                {value === allOption.value && (
                  <svg width="13" height="13" fill="none" stroke="var(--rh-orange)" strokeWidth="2.4" viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            )}

            {filtered.length === 0 && !allOption ? (
              <div style={{ padding: '12px', fontSize: 13, color: 'var(--rh-text-faint)', textAlign: 'center' }}>
                No matches.
              </div>
            ) : (
              filtered.map((o) => {
                const isActive = String(o.value) === String(value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => handleSelect(o.value)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      width: '100%', padding: '8px 12px', border: 'none', background: 'transparent',
                      cursor: 'pointer', textAlign: 'left', fontSize: 13,
                      color: isActive ? 'var(--rh-orange)' : 'var(--rh-text)',
                      fontWeight: isActive ? 600 : 500,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--rh-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.label}
                      {o.hint && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--rh-text-faint)', fontWeight: 400 }}>{o.hint}</span>}
                    </span>
                    {isActive && (
                      <svg width="13" height="13" fill="none" stroke="var(--rh-orange)" strokeWidth="2.4" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}

            {filtered.length === 0 && allOption && (
              <div style={{ padding: '12px', fontSize: 13, color: 'var(--rh-text-faint)', textAlign: 'center' }}>
                No redditors match "{query}".
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
